import {
    AfterViewInit,
    Component,
    ComponentMirror,
    ComponentRef,
    DestroyRef,
    ElementRef,
    EnvironmentInjector,
    EventEmitter,
    InjectionToken,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Provider,
    Type,
    ViewContainerRef,
    inject,
    reflectComponentType,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { IConstant } from '@frontend/oxygen/shared-utils';

import { IFreebetsPopupDetails } from '../../../core/services/cms/models/system-config';
import { type DefaultFreeBetsNotificationComponent } from './default.free-bets-notification.component';

export const FreeBetsNotificationComponentToken = new InjectionToken<DefaultFreeBetsNotificationComponent & Type<any>>(
    'FreeBetsNotificationComponentToken',
);

export function provideFreeBetsNotificationComponent(cmp: typeof DefaultFreeBetsNotificationComponent): Provider {
    return {
        provide: FreeBetsNotificationComponentToken,
        useValue: cmp,
    } as Provider;
}

type ExtractOutputType<TComponent, TKey extends keyof TComponent> = TComponent[TKey] extends EventEmitter<infer T> ? T : never;

@Component({
    selector: 'free-bets-notification',
    template: '<ng-container #baseRef></ng-container>',
    standalone: true,
})
export class FreeBetsNotificationComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() isBetslip: boolean;
    @Input() hasFreeBets: boolean;
    @Input() hasBetTokens: boolean;
    @Input() hasFanzones: boolean;
    @Input() isNoSelections: boolean;
    @Input() freebetsConfig: IFreebetsPopupDetails;
    @Output() readonly hide: EventEmitter<boolean> = new EventEmitter();
    private injector = inject(Injector);
    private envInjector = inject(EnvironmentInjector);
    private destroyRef = inject(DestroyRef);
    private readonly vcr = viewChild.required('baseRef', { read: ViewContainerRef });
    private cmpToken = inject(FreeBetsNotificationComponentToken);

    private cmpRef?: ComponentRef<any>;

    private compMetaData: ComponentMirror<any>;

    constructor(private elem: ElementRef) {
        this.compMetaData = reflectComponentType(this.cmpToken) ?? ({} as ComponentMirror<typeof DefaultFreeBetsNotificationComponent>);
    }

    ngOnChanges() {
        this.syncInputs();
    }

    ngAfterViewInit() {
        if (!this.cmpToken) {
            throw new Error('Please provide a freebet notification component!');
        }

        this.cmpRef = this.vcr().createComponent(this.cmpToken, {
            injector: this.injector,
            environmentInjector: this.envInjector,
        });

        this.syncInputs();
        this.syncOutputs();
    }

    private syncInputs() {
        if (this.cmpRef && this.compMetaData?.inputs) {
            this.compMetaData.inputs.forEach((input: IConstant) => {
                if (this.cmpRef) this.cmpRef.setInput(input.propName, this[input.propName as keyof FreeBetsNotificationComponent]);
            });

            const dataCrlat = this.elem?.nativeElement?.getAttribute('data-crlat') || this.elem?.nativeElement?.getAttribute('host-data-crlat') || '';
            const hostClasses = this.elem?.nativeElement?.className || this.elem?.nativeElement?.getAttribute('host-classes') || '';
            this.elem.nativeElement.setAttribute('host-classes', hostClasses);
            this.elem.nativeElement.setAttribute('host-data-crlat', dataCrlat);
            this.cmpRef.setInput('dataCrlat', dataCrlat);
            this.cmpRef.setInput('hostClasses', hostClasses);

            this.elem.nativeElement.removeAttribute('class');
            this.elem.nativeElement.removeAttribute('data-crlat');
        }
    }

    private syncOutputs() {
        if (this.compMetaData?.outputs) {
            this.compMetaData.outputs.forEach((output: IConstant) => {
                const outputAction = output.propName;
                this.cmpRef?.instance?.[outputAction]
                    ?.pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((x: ExtractOutputType<typeof this.cmpRef.instance, typeof outputAction>) => {
                        (this[outputAction as keyof FreeBetsNotificationComponent] as EventEmitter<any>).emit(x);
                    });
            });
        }
    }

    ngOnDestroy(): void {
        this.cmpRef?.destroy();
    }
}
